import { Injectable, Optional } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, tap, withLatestFrom } from 'rxjs/operators';
import { useLocalization } from '@shared/localization';
import type { SearchableSingleSelectorItem } from '@shared/ui';
import { RetrieveMethodTypeErrorCode, RetrieveMethodTypeException } from '@protocols/use-cases';
import { MethodTypeConfigService } from '@protocols/domain';

import { AssetTypeEffects } from '../../../effects/asset-type.effects';
import {
  LabelConsolidationFailureEffects,
  LabelFailureEvent,
} from '../../../effects/label-consolidation-failure.effects';
import { MethodTypeEditorEffects } from '../../../effects/method-type-editor.effects';
import { LabelConfigState } from '../../../state/label-config.state';
import { LabelState } from '../../../state/label.state';
import { MethodTypeEditorState } from '../../../state/method-type-editor.state';
import { MethodTypeState } from '../../../state/method-type.state';
import { StagedPipelinesState } from '../../../state/staged-pipelines.state';
import { StagedResourcesState } from '../../../state/staged-resources.state';

@Injectable()
export class MethodTypeEditorFacade {
  constructor(
    private readonly editorEffects: MethodTypeEditorEffects,
    private readonly state: MethodTypeEditorState,
    private readonly assetTypeEffects: AssetTypeEffects,
    private readonly labelState: LabelState,
    private readonly labelConfigState: LabelConfigState,
    private readonly labelFailureEffects: LabelConsolidationFailureEffects,
    private readonly stagedPipelinesState: StagedPipelinesState,
    private readonly stagedResourcesState: StagedResourcesState,
    @Optional() private readonly methodTypeState: MethodTypeState | null = null
  ) {
    this.currentLanguage$ = useLocalization().currentLanguage$;
    this.wireMethodTypeBinding();
  }

  /**
   * Publishes the editing session's method type identity (id + name) into the
   * shared `MethodTypeState`, when one is provided. Downstream pipeline
   * editing reads it as "the workflow's bound method type" — it gates the
   * per-option color action and keys the staged label configs. A session
   * without a name yet stays unbound.
   */
  private wireMethodTypeBinding(): void {
    if (!this.methodTypeState) return;
    // ComponentStore observables complete on the store's destroy$, so plain
    // subscribe() here does not leak.
    this.state
      .select((s) => ({ id: s.id, name: s.name }))
      .pipe(distinctUntilChanged((a, b) => a.id === b.id && a.name === b.name))
      .subscribe(({ id, name }) => {
        this.methodTypeState?.setMethodType(id && name ? { id, name } : null);
      });
  }

  /** Current app language — drives the localized sample-name chip display. */
  public readonly currentLanguage$: Observable<string>;

  // ============================================================================
  // Selectors — passthrough
  // ============================================================================

  public readonly name$ = this.state.name$;
  public readonly sampleNames$ = this.state.sampleNames$;
  public readonly acquisitionFields$ = this.state.acquisitionFields$;
  public readonly nameError$ = this.state.nameError$;
  public readonly assetTypeError$ = this.state.assetTypeError$;
  public readonly configError$ = this.state.configError$;
  public readonly manualConfigMode$ = this.state.manualConfigMode$;
  public readonly customConfigValue$ = this.state.customConfigValue$;
  public readonly canExitCustomConfig$ = this.state.canExitCustomConfig$;
  public readonly loading$ = this.state.loading$;

  /**
   * In-flight signal for the whole save cascade as seen from the editor: the
   * row save, the label batch, the staged-pipeline batch, the resource walk
   * and the config tail each own a `saving` slice
   * (`PipelineEditorFacade.saving$`'s composition approach, widened to every
   * phase the method-type flow runs). `SaveProtocolCascadeEffects` is
   * deliberately NOT injected — stories omit it; the slices carry the signal.
   */
  public readonly saving$: Observable<boolean> = combineLatest([
    this.state.saving$,
    this.labelState.saving$,
    this.stagedPipelinesState.saving$,
    this.stagedResourcesState.saving$,
    this.labelConfigState.saving$,
  ]).pipe(
    map((flags) => flags.some(Boolean)),
    distinctUntilChanged()
  );

  /**
   * Whether cancelling now would discard user work — row edits, pipeline
   * assignments, staged pipeline/resource/label drafts or label configs.
   * Compares the effects' live session signature against the baseline captured
   * at seed. False until a seed completes (`baselineSignature` still `null`),
   * so a failed load never warns.
   */
  public readonly hasChanges$: Observable<boolean> = this.state.select(
    this.editorEffects.currentSignature$,
    this.state.baselineSignature$,
    (current, baseline) => baseline !== null && current !== baseline
  );

  // ============================================================================
  // Selectors — asset-type catalog (composed from AssetTypeEffects)
  // ============================================================================

  /**
   * Catalog page projected into selector items. The currently selected asset
   * type is deliberately NOT prepended when it is missing from the page:
   * `ts-searchable-single-selector` renders the `selected` input through its
   * own independent display-text element (its `mat-select-trigger` is
   * transparent), so an existing method type's asset type displays correctly
   * even before the catalog page contains it.
   */
  public readonly assetTypeOptions$: Observable<SearchableSingleSelectorItem[]> =
    this.assetTypeEffects.assetTypes$.pipe(map((assetTypes) => assetTypes.map(({ id, name }) => ({ id, name }))));

  public readonly assetTypesLoaded$ = this.assetTypeEffects.assetTypesLoaded$;

  public readonly assetTypeSearchTerm$: Observable<string> = this.assetTypeEffects.searchTerm$.pipe(
    map((term) => term ?? '')
  );

  /**
   * The selected asset type shaped for the selector. The state's resolved
   * `assetTypeName` wins; when the load could not resolve it, the catalog is
   * consulted, and as a last resort the raw id is shown (the query's
   * documented contract: a failed name lookup never hides the selection).
   */
  public readonly selectedAssetTypeItem$: Observable<SearchableSingleSelectorItem | null> = combineLatest([
    this.state.assetTypeId$,
    this.state.assetTypeName$,
    this.assetTypeEffects.assetTypes$,
  ]).pipe(
    map(([assetTypeId, assetTypeName, catalog]) => {
      if (!assetTypeId) return null;
      const catalogName = catalog.find((assetType) => assetType.id === assetTypeId)?.name;
      return { id: assetTypeId, name: assetTypeName || catalogName || assetTypeId };
    })
  );

  // ============================================================================
  // Selectors — error dispatch
  // ============================================================================

  /**
   * Unified error stream for the page banner. Load failures win, then the
   * row/join save failure, then the first staged-pipeline row failure, then
   * the first resource row failure (the cascade is fail-fast, so at most one
   * batch holds exceptions). Label and label-config row failures surface as
   * snackbars instead (`labelSaveFailed$` / `labelConfigSaveFailed$`,
   * pipeline-editor parity). Effects/cascade never write i18n keys; they
   * record typed exceptions on the owner states and the facade dispatches
   * them into its own namespace here.
   */
  public readonly error$: Observable<string | null> = combineLatest([
    this.state.loadException$,
    this.state.saveException$,
    this.stagedPipelinesState.saveExceptions$,
    this.stagedResourcesState.saveExceptions$,
  ]).pipe(
    map(([loadException, saveException, pipelineExceptions, resourceExceptions]) => {
      if (loadException) return this.loadErrorToKey(loadException);
      if (saveException) return 'method_type_editor.errors.save_failed';
      if (Object.keys(pipelineExceptions).length > 0) return 'method_type_editor.errors.pipeline_save_failed';
      if (Object.keys(resourceExceptions).length > 0) return 'method_type_editor.errors.resource_save_failed';
      return null;
    }),
    distinctUntilChanged()
  );

  private loadErrorToKey(error: Error): string {
    if (
      error instanceof RetrieveMethodTypeException &&
      error.code === RetrieveMethodTypeErrorCode.METHOD_TYPE_NOT_FOUND
    ) {
      return 'method_type_editor.errors.method_type_not_found';
    }
    return 'method_type_editor.errors.load_failed';
  }

  // ============================================================================
  // Selectors — per-row label failure events (pipeline-editor facade parity)
  // ============================================================================

  /**
   * Each failing label-save emits a `{ name, error }` pair to toast (shared
   * `LabelConsolidationFailureEffects` derivation — display name already
   * resolved in the current app language).
   */
  public readonly labelSaveFailed$: Observable<LabelFailureEvent> = this.labelFailureEffects.labelSaveFailed$;

  /** Per-row label-config tail failures — see `labelSaveFailed$`. */
  public readonly labelConfigSaveFailed$: Observable<LabelFailureEvent> =
    this.labelFailureEffects.labelConfigSaveFailed$;

  // ============================================================================
  // Commands
  // ============================================================================

  /** Triggered by the component on init — I/O is driven by lifecycle, not DI. */
  public readonly initialize = this.state.effect<void>((trigger$) =>
    trigger$.pipe(tap(() => this.assetTypeEffects.initializeAssetTypes()))
  );

  public readonly setName = this.state.setName;

  public readonly selectAssetType = this.state.effect((item$: Observable<SearchableSingleSelectorItem>) =>
    item$.pipe(
      tap((item: SearchableSingleSelectorItem) => this.state.setAssetType({ id: String(item.id), name: item.name }))
    )
  );

  /** Catalog search (alias of the list state's updater; triggers the reload). */
  public readonly searchAssetTypes = this.assetTypeEffects.setSearchTerm;

  /** Catalog pagination (CONTINUOUS loading appends the next page). */
  public readonly loadMoreAssetTypes = this.assetTypeEffects.loadMoreAssetTypes;

  public readonly addSampleName = this.state.addSampleName;
  public readonly removeSampleName = this.state.removeSampleName;
  public readonly updateSampleName = this.state.updateSampleName;

  /**
   * Activate manual JSON config mode, seeding the editor from the current
   * predefined sample names (`{ defaultSamples }`). Reversible via
   * `exitCustomConfig` while the draft stays conventional.
   */
  public readonly enterCustomConfig = this.state.effect<void>((trigger$) =>
    trigger$.pipe(
      withLatestFrom(this.state.sampleNames$),
      tap(([, sampleNames]: [void, Record<string, string>[]]) =>
        this.state.enterCustomConfig({ value: MethodTypeConfigService.buildConfig(sampleNames) })
      )
    )
  );

  /**
   * Exit manual mode by parsing the JSON draft back into the chips (gated by
   * `canExitCustomConfig$`). Parse-back deliberately diverges from the
   * pipeline step editor, whose exit discards the manual draft and reverts to
   * the pre-manual form state: the pipeline's compatibility check is key-only
   * (values never validated) so parse-back is unsafe there, while the
   * method-type conventionality check validates the full structure, making
   * parse-back trivially safe and truly lossless.
   */
  public readonly exitCustomConfig = this.state.exitCustomConfig;

  /** Record the JSON editor's latest `{ value, valid }` parse state. */
  public readonly setCustomConfig = this.state.setCustomConfig;

  public readonly addAcquisitionField = this.state.addAcquisitionField;
  public readonly removeAcquisitionField = this.state.removeAcquisitionField;

  /**
   * Save is validate-then-handoff: the button is never disabled, so every
   * click re-runs BOTH checks (each paints its own i18n error key). Only a
   * fully valid draft clears the errors and emits the one-shot `saved`
   * outcome — persistence belongs to the consumer (the page runs the
   * protocol cascade on `saved`).
   */
  public readonly save = this.state.effect<void>((trigger$) =>
    trigger$.pipe(
      withLatestFrom(
        this.state.name$,
        this.state.assetTypeId$,
        this.state.manualConfigMode$,
        this.state.customConfigValid$
      ),
      tap(([, name, assetTypeId, manualConfigMode, configValid]: [void, string, string | null, boolean, boolean]) => {
        const nameMissing = name.trim() === '';
        const assetTypeMissing = !assetTypeId;
        // In manual mode the JSON must parse to an object, else the persisted
        // config would be unusable — block the save and paint the error.
        const configInvalid = manualConfigMode && !configValid;
        if (nameMissing) this.state.setNameError('method_type_editor.errors.name_required');
        if (assetTypeMissing) this.state.setAssetTypeError('method_type_editor.errors.asset_type_required');
        if (configInvalid) this.state.setConfigError('method_type_editor.errors.config_invalid');
        if (nameMissing || assetTypeMissing || configInvalid) return;

        this.state.setNameError(null);
        this.state.setAssetTypeError(null);
        this.state.setConfigError(null);
        this.state.confirm({ kind: 'saved' });
      })
    )
  );

  /**
   * Cancel is a pure handoff: emit the one-shot `cancelled` outcome on the
   * shared state. The COMPONENT runs the discard-confirmation dialog first;
   * the consumer (route page facade) decides what to do — navigate back,
   * discard the draft.
   */
  public readonly cancel = this.state.effect<void>((trigger$) =>
    trigger$.pipe(tap(() => this.state.confirm({ kind: 'cancelled' })))
  );
}
